import {
    BoltOutlined,
    BusinessOutlined,
    DescriptionOutlined,
    LocationOnOutlined,
    PeopleOutline,
} from '@mui/icons-material';
import {Avatar, Box, Grid, IconButton, Typography} from '@mui/material';
import {PhotoCameraOutlined} from '@mui/icons-material';
import {FC, ReactNode, useState} from 'react';
import {FormattedMessage, useIntl} from 'react-intl';
import {useParams} from 'react-router-dom';
import {businessesApi, chargersApi, contractsApi, locationsApi, usersApi} from 'src/api';
import {useAppDispatch, useAppSelector} from 'src/app/hooks';
import {PageHeader} from 'src/components/common/PageHeader';
import {UserTypeEnum} from 'src/enums/common';
import {KpiCard} from 'src/pages/EnergyConsumptionReport/components/KpiCard';
import AMPModal from 'src/components/common/AMPModal';
import {DropZoneModal} from 'src/components/common/DropZoneModal';
import {EditBusinessForm} from 'src/components/forms/EditBusinessForm';
import {BusinessTerms} from 'src/components/businesses/BusinessTerms';
import {BusinessTermsRfid} from 'src/components/businesses/BusinessTermsRfid';
import {BusinessSignature} from 'src/components/businesses/BusinessSignature';
import {useCan} from 'src/permissions/hooks';
import {BusinessDTO} from 'src/types';
import {preloaderChangeStatus, updateUserPhoto} from 'src/stores/common/commonSlice';
import {isErrorObject} from 'src/utils/helper';
import {NotifyError} from 'src/utils/notification';
import {blankPhoto} from 'src/utils/consts';
import theme from 'src/theme';
import {editBtnSx, fieldLabelSx, fieldValueSx, panelSx, panelTitleSx} from './BusinessProfile.sx';
import {EditIcon} from 'src/components/icons/EditIcon';

/** One count, asked for as cheaply as the search endpoints allow: a page of one, read for its total. */
const COUNT_PARAMS = {page: 0, size: 1};

const Field: FC<{label: ReactNode; value?: ReactNode}> = ({label, value}) => (
    <Box sx={{mb: 2}}>
        <Typography sx={fieldLabelSx}>{label}</Typography>
        <Typography sx={fieldValueSx}>{value || '-'}</Typography>
    </Box>
);

/**
 * A single business.
 *
 * Its own page rather than the user screen the sidebar used to point at: that screen answers "who
 * owns this business", which is one field of the answer to "what is this business". The counts come
 * first because they are what the question usually is - how much is in here - and the identifying
 * details below, in the order somebody reads them out loud.
 */
export const BusinessProfile: FC = () => {
    const intl = useIntl();
    const dispatch = useAppDispatch();
    const {id} = useParams();
    const businessId = Number(id);
    const ownBusiness = useAppSelector((state) => state.common.authInfo?.business?.id);

    const {data: business, isFetching} = businessesApi.useGetBusinessByIdQuery({businessId});
    // The same permission the endpoint behind the form checks, so the control appears exactly when
    // pressing it would work.
    const canUpdate = useCan('businesses.update');
    const [editing, setEditing] = useState(false);
    const [logoOpen, setLogoOpen] = useState(false);
    const [logo, setLogo] = useState<File[] | null>(null);
    const [uploading, setUploading] = useState(false);
    const [uploadLogo] = businessesApi.useAddBusinessLogoMutation();

    const submitLogo = async () => {
        if (!logo) {
            return;
        }
        try {
            setUploading(true);
            dispatch(preloaderChangeStatus(true));
            const formData = new FormData();
            formData.append('file', logo[0]);
            const updated = await uploadLogo({formData, businessId}).unwrap();
            // Only when it is the business this account belongs to: the avatar in the toolbar is
            // theirs, and a platform administrator editing someone else's logo must not replace it.
            if (ownBusiness === businessId) {
                dispatch(updateUserPhoto(updated.logo));
            }
        } catch (error) {
            if (isErrorObject(error)) {
                NotifyError(error.data.message);
            }
        } finally {
            setUploading(false);
            setLogoOpen(false);
            dispatch(preloaderChangeStatus(false));
        }
    };

    const locations = locationsApi.useGetLocationsQuery({data: {businessId}, params: COUNT_PARAMS});
    const chargers = chargersApi.useGetChargersQuery({data: {businessId}, params: COUNT_PARAMS});
    const contracts = contractsApi.useGetContractsQuery({data: {businessId}, params: COUNT_PARAMS});
    const users = usersApi.useGetUsersQuery({
        data: {
            businessId,
            userTypes: [UserTypeEnum.PRIVATE_DRIVER, UserTypeEnum.EMPLOYEE, UserTypeEnum.COMPANY],
        },
        params: COUNT_PARAMS,
    });

    const owner = business?.businessOwnerInfo;
    const address = business?.address;

    const count = (total?: number) => (total === undefined ? '' : String(total));

    return (
        <Box sx={{pb: 4}}>
            <PageHeader
                title={business?.name ?? intl.formatMessage({id: 'businesses'})}
                icon={<BusinessOutlined/>}
                onlyTitle
            />

            <Grid container spacing={2} sx={{mb: 3}}>
                <Grid item xs={12} sm={6} md={3}>
                    <KpiCard
                        label={intl.formatMessage({id: 'locations'})}
                        value={count(locations.data?.page?.totalElements)}
                        icon={<LocationOnOutlined fontSize="small"/>}
                        loading={locations.isFetching}
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <KpiCard
                        label={intl.formatMessage({id: 'chargers'})}
                        value={count(chargers.data?.page?.totalElements)}
                        icon={<BoltOutlined fontSize="small"/>}
                        loading={chargers.isFetching}
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <KpiCard
                        label={intl.formatMessage({id: 'users'})}
                        value={count(users.data?.page?.totalElements)}
                        icon={<PeopleOutline fontSize="small"/>}
                        loading={users.isFetching}
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <KpiCard
                        label={intl.formatMessage({id: 'contracts'})}
                        value={count(contracts.data?.page?.totalElements)}
                        icon={<DescriptionOutlined fontSize="small"/>}
                        loading={contracts.isFetching}
                    />
                </Grid>
            </Grid>

            <Grid container spacing={2} sx={{mb: 3}}>
                <Grid item xs={12} md={4}>
                    <Box sx={panelSx}>
                        {/* Where the terms panels keep theirs: the record a panel shows is edited
                            from that panel's own corner, not from a page-wide button that names one
                            of the four things on screen. */}
                        {canUpdate && (
                            <IconButton
                                sx={editBtnSx}
                                onClick={() => setEditing(true)}
                            >
                                <EditIcon/>
                            </IconButton>
                        )}
                        <Typography sx={panelTitleSx}>
                            <FormattedMessage id="businessInformation"/>
                        </Typography>
                        {/* The logo carries its own control, on the image, because the pencil in
                            the corner already means "edit this record" - two identical pencils in one
                            panel say nothing about which changes what. */}
                        <Box sx={{position: 'relative', width: 56, mb: 2}}>
                            <Avatar
                                src={business?.logo?.url || blankPhoto}
                                variant="rounded"
                                sx={{width: 56, height: 56}}
                            />
                            {canUpdate && (
                                <IconButton
                                    size="small"
                                    onClick={() => setLogoOpen(true)}
                                    sx={{
                                        position: 'absolute',
                                        right: -10,
                                        bottom: -10,
                                        padding: '4px',
                                        backgroundColor: theme.palette.background.default,
                                        border: `1px solid ${theme.palette.neutral[200]}`,
                                        '&:hover': {backgroundColor: theme.palette.neutral[100]},
                                    }}
                                >
                                    <PhotoCameraOutlined sx={{fontSize: 14}}/>
                                </IconButton>
                            )}
                        </Box>
                        <Field label={<FormattedMessage id="name"/>} value={business?.name}/>
                        <Field
                            label={<FormattedMessage id="backendPricePerUser"/>}
                            value={business && `${business.userPerMonthCost ?? 0} ${business.userPerMonthCurrency ?? ''}`}
                        />
                        {/* Stored in cents, the same way the RFID terms form writes it. */}
                        <Field
                            label={<FormattedMessage id="rfidCardPrice"/>}
                            value={business && `${(business.rfidCost ?? 0) / 100} ${business.rfidCostCurrency ?? ''}`}
                        />
                    </Box>
                </Grid>

                <Grid item xs={12} md={4}>
                    <Box sx={panelSx}>
                        <Typography sx={panelTitleSx}>
                            <FormattedMessage id="owner"/>
                        </Typography>
                        <Field
                            label={<FormattedMessage id="name"/>}
                            value={owner && [owner.firstName, owner.lastName].filter(Boolean).join(' ')}
                        />
                        <Field label={<FormattedMessage id="email"/>} value={owner?.emailAddress}/>
                        <Field label={<FormattedMessage id="phoneNumber"/>} value={owner?.phoneNumber}/>
                        <Field label={<FormattedMessage id="language"/>} value={owner?.language}/>
                    </Box>
                </Grid>

                <Grid item xs={12} md={4}>
                    <Box sx={panelSx}>
                        <Typography sx={panelTitleSx}>
                            <FormattedMessage id="address"/>
                        </Typography>
                        <Field
                            label={<FormattedMessage id="billingAddressStreet"/>}
                            value={address && [address.street, address.houseNumber].filter(Boolean).join(' ')}
                        />
                        <Field label={<FormattedMessage id="billingAddressCity"/>} value={address?.city}/>
                        <Field label={<FormattedMessage id="billingAddressZipCode"/>} value={address?.zipCode}/>
                        <Field label={<FormattedMessage id="country"/>} value={address?.country}/>
                    </Box>
                </Grid>
            </Grid>

            {business && (
                <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                        <Box sx={panelSx}>
                            <BusinessTerms data={business as BusinessDTO}/>
                        </Box>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Box sx={panelSx}>
                            <BusinessTermsRfid data={business as BusinessDTO}/>
                        </Box>
                    </Grid>
                </Grid>
            )}

            {business && (
                <Grid container spacing={2} sx={{mt: 1}}>
                    <Grid item xs={12}>
                        <Box sx={panelSx}>
                            <BusinessSignature data={business as BusinessDTO}/>
                        </Box>
                    </Grid>
                </Grid>
            )}

            {business && (
                <AMPModal
                    handleClose={() => setEditing(false)}
                    open={editing}
                    title={intl.formatMessage({id: 'editBusiness'})}
                >
                    <EditBusinessForm
                        businessData={business as BusinessDTO}
                        handleShowModal={(status: boolean) => () => setEditing(status)}
                    />
                </AMPModal>
            )}

            <DropZoneModal
                open={logoOpen}
                handleClose={() => setLogoOpen(false)}
                title={intl.formatMessage({id: 'editBusiness'})}
                handleFile={setLogo}
                handleSubmit={submitLogo}
                submitIsBlocked={uploading}
                isImage
            />

            {!isFetching && !business && (
                <Typography sx={{mt: 3, color: 'text.secondary'}}>
                    <FormattedMessage id="noData"/>
                </Typography>
            )}
        </Box>
    );
};
